/**
 * @file ColumnFilterMenu.js
 * @version 2.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-26
 * @modified 2026-09-23
 * @description The popover behind useColumnFilters: a searchable tick list of
 *              the values in one column, anchored under its header.
 *
 *              Portalled to the body rather than rendered inside the <th>. A
 *              table header is sticky and its cells clip, so a menu inside one
 *              is cropped at the first row — and a popover that opens
 *              underneath the data it is filtering is no use at all.
 *
 *              A column can declare `sections` — several filter keys under one
 *              header, stacked and collapsible, first one open. A customer
 *              order's Status cell is six independent facts (on a PO, sent,
 *              confirmed, planned, delivered, priced) and this is where they
 *              are narrowed, instead of six dropdowns in the filter row.
 *
 *              Counts are shown against each value because they answer the
 *              question people actually have when they open this: not "does
 *              PARTIALLY_RECEIVED exist" but "how many are there" — and since
 *              the facets are computed server-side over the whole result set,
 *              that count is the real one rather than this page's share of it.
 */

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import TickCheckbox from './TickCheckbox';
import './ColumnFilterMenu.css';

function ColumnFilterMenu({ cols }) {
  const { menu, definitions } = cols;
  const key = menu.key;
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState(null);
  const boxRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { setSearch(''); }, [key]);

  // Measured after paint so the box has a height to flip against.
  useLayoutEffect(() => {
    if (!key || !menu.anchor) { setPosition(null); return; }
    const rect = menu.anchor.getBoundingClientRect();
    const box = boxRef.current;
    const height = box?.offsetHeight || 320;
    const width = box?.offsetWidth || 260;
    // Keep it on screen: a header near the right edge or low in the window
    // would otherwise open the menu off it.
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    const below = rect.bottom + 4;
    const top = below + height > window.innerHeight - 8
      ? Math.max(8, rect.top - height - 4)
      : below;
    setPosition({ top, left: Math.max(8, left) });
  }, [key, menu.anchor]);

  useEffect(() => {
    if (!key) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) cols.closeMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') cols.closeMenu(); };
    // Close on scroll: the menu is positioned once against the header, so a
    // scrolled table would leave it pointing at nothing.
    //
    // Except its OWN scroll. The listener is on the capture phase so it sees
    // scrolling anywhere, which includes .cfm-list — the value list inside this
    // very menu. A column with more values than fit could therefore not be
    // scrolled at all: the first wheel tick closed the thing being scrolled.
    // The page and the table are still caught, because a scroll event on the
    // document targets the document, which is not inside the box.
    const onScroll = (e) => {
      if (boxRef.current && e.target instanceof Node && boxRef.current.contains(e.target)) return;
      cols.closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [key, cols]);

  const sections = useMemo(() => (key ? cols.sectionsFor(key) : []), [key, cols]);
  const sectioned = sections.length > 1 || !sections[0]?.implicit;

  // Which sections are open. Reset whenever the menu moves to another header,
  // so a menu always opens showing its first section rather than whatever the
  // user last left expanded on a different column.
  const [open, setOpen] = useState({});
  useEffect(() => {
    setOpen(Object.fromEntries(sections.map((sec) => [sec.key, !!sec.defaultOpen])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // A value and the text for it are not always the same thing. Filtering
  // contacts means filtering by account id, and a menu listing "2517" is no
  // use to anybody — so an option may carry its own `label`, which is what
  // gets shown AND what gets searched. Callers that pass no label are
  // unchanged: the value is the label.
  const textOf = (o) => String(o.label ?? o.value);

  if (!key || !definitions[key]) return null;

  const term = search.trim().toLowerCase();

  /** One section's options, its selection, and what the head should say. */
  const stateOf = (sectionKey) => {
    const options = cols.optionsFor(sectionKey);
    const shown = term ? options.filter((o) => textOf(o).toLowerCase().includes(term)) : options;
    const chosen = cols.selected[sectionKey];
    return {
      options,
      shown,
      isChecked: (value) => (Array.isArray(chosen) ? chosen.includes(value) : true),
      checkedCount: Array.isArray(chosen)
        ? options.filter((o) => chosen.includes(o.value)).length
        : options.length
    };
  };

  const totalOptions = sections.reduce((n, sec) => n + stateOf(sec.key).options.length, 0);

  const valueList = (sectionKey, state) => (
    <div className="cfm-list">
      {state.shown.length === 0 && (
        <p className="cfm-empty">
          {term ? `No values match “${search}”.` : 'No values for this filter yet.'}
        </p>
      )}
      {state.shown.map((option) => (
        <label key={option.value} className="cfm-option">
          <TickCheckbox
            id={`cfm-${sectionKey}-${option.value}`}
            checked={state.isChecked(option.value)}
            onChange={() => cols.toggle(sectionKey, option.value)}
            ariaLabel={textOf(option)}
          />
          <span className="cfm-value" title={textOf(option)}>{textOf(option)}</span>
          <span className="cfm-n">{option.count}</span>
        </label>
      ))}
    </div>
  );

  return ReactDOM.createPortal(
    <div
      ref={boxRef}
      className="col-filter-menu"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={`Filter by ${definitions[key].label}`}
    >
      <div className="cfm-head">
        <span className="cfm-title">{definitions[key].label}</span>
        {!sectioned && (
          <span className="cfm-count">
            {stateOf(key).checkedCount} of {stateOf(key).options.length}
          </span>
        )}
      </div>

      <div className="cfm-search">
        <span className="material-symbols-rounded">search</span>
        <input
          ref={searchRef}
          type="text"
          value={search}
          placeholder="Search values…"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* "Clear all" means an empty selection, which on most screens is a real
          state — nothing ticked, no rows. On a screen where an empty selection
          IS the absence of a filter, it would do the opposite of its label, so
          such a caller opts out rather than shipping a button that lies.

          On a sectioned menu these sit in each section's own head: the two
          questions have separate answers, and one pair of buttons could only
          have acted on whichever the user meant. */}
      {!sectioned && (
        <div className="cfm-bulk">
          <button type="button" onClick={() => cols.selectAll(key)}>Select all</button>
          {cols.allowClearAll !== false && (
            <button type="button" onClick={() => cols.clearAll(key)}>Clear all</button>
          )}
        </div>
      )}

      {!sectioned && valueList(key, stateOf(key))}

      {sectioned && (
        <div className="cfm-sections">
          {sections.map((section) => {
            const state = stateOf(section.key);
            // A search that matches nothing in a closed section should say so
            // rather than leave it looking unsearched.
            const isOpen = term ? state.shown.length > 0 : !!open[section.key];
            const narrowed = Array.isArray(cols.selected[section.key]);
            return (
              <section key={section.key} className={`cfm-section${isOpen ? ' open' : ''}`}>
                <button
                  type="button"
                  className="cfm-section-head"
                  aria-expanded={isOpen}
                  onClick={() => setOpen((prev) => ({ ...prev, [section.key]: !prev[section.key] }))}
                >
                  <span className="material-symbols-rounded cfm-chevron">chevron_right</span>
                  <span className="cfm-section-label">{section.label}</span>
                  {narrowed && <span className="cfm-section-dot" title="Filtered" />}
                  <span className="cfm-count">{state.checkedCount} of {state.options.length}</span>
                </button>
                {isOpen && (
                  <>
                    <div className="cfm-bulk">
                      <button type="button" onClick={() => cols.selectAll(section.key)}>Select all</button>
                      {cols.allowClearAll !== false && (
                        <button type="button" onClick={() => cols.clearAll(section.key)}>Clear all</button>
                      )}
                    </div>
                    {valueList(section.key, state)}
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* The scope is the whole result set, not the page — worth saying,
          because the counts beside each value are the only visible evidence of
          it and they are easy to read as page counts. */}
      <p className="cfm-scope">
        {totalOptions === 0
          ? 'No values available for this column yet — run a search first.'
          : cols.hasFacets
            ? 'Filters every matching record, not just this page. Stays applied as you search and paginate.'
            : `Filters the ${cols.allRows.length} rows on this page.`}
      </p>
    </div>,
    document.body
  );
}

export default ColumnFilterMenu;
